import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { AuthShell } from "@/components/AuthShell";
import { Field } from "@/pages/auth/Login";
import { guestApi, isTransportError, USE_FIXTURES } from "@/lib/api";

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentMessage, setSentMessage] = useState("");
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await guestApi.forgotPassword({ email });
      setSentMessage(
        res?.message || "If an account exists for that email, recovery instructions are ready.",
      );
    } catch (err) {
      if (USE_FIXTURES && isTransportError(err)) {
        setSentMessage("Fixture mode: use reset code 111111.");
      } else {
        setError(
          err?.response?.data?.message ||
            err?.apiMessage ||
            err?.message ||
            "Could not start password recovery. Please try again shortly.",
        );
        setSubmitting(false);
        return;
      }
    }
    setSubmitting(false);
    setSent(true);
  };

  return (
    <AuthShell
      eyebrow="Forgot password · Recovery"
      title={<>Reset your<br /><span className="italic">access</span>.</>}
      subtitle="Enter the email you used to sign up. We'll send you a 6-digit code to reset your password."
    >
      {sent ? (
        <div data-testid="forgot-sent">
          <p className="text-[10px] uppercase tracking-[0.2em] text-primary">Sent</p>
          <p className="mt-4 font-display text-2xl leading-tight tracking-tight md:text-3xl">
            {sentMessage || "If an account exists for that email, a code is on its way."}
          </p>
          <button
            type="button"
            onClick={() => navigate(`/reset-password?email=${encodeURIComponent(email)}`)}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5"
          >
            Enter code <ArrowRight size={16} />
          </button>
          <p className="mt-6 text-xs text-muted-foreground">
            Didn't get one? Check spam, or{" "}
            <Link to="/contact-us" className="underline">contact support</Link>.
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-8" data-testid="forgot-form">
          <Field label="Email" type="email" value={email} onChange={setEmail} testId="forgot-email" />
          {error && <p className="text-xs text-primary">{error}</p>}
          <button
            type="submit"
            data-testid="forgot-submit"
            disabled={submitting}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send code"} <ArrowRight size={16} />
          </button>
          <p className="pt-2 text-center text-sm text-muted-foreground">
            Remembered it?{" "}
            <Link to="/login" className="font-medium text-foreground underline">
              Log in
            </Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
};

export default ForgotPassword;
